import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { WidgetConfigurationItemObj } from '@wizeoo/akm-message-lib';
import { FeiKeyDef, WizConstants } from '@wizeoo/akm-compute-lib';
import { IWidget } from '../../manager/widgets/i-widget';
import { FreedataArgsVO } from  '../../vo/core/freedataargs.vo';
import { WIDGET_GLOBAL_KIND } from '../../model/enums';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { Model } from '../../model/model';
import { WizTranslationManager } from '@wizeoo/akm-component-lib';
import { ViewData } from '../../model/view.data';

/**
 * un champ de configuration du pivot. Va encapsuler le pivotKeyDef source
 * pour permettre n instances du même champs
 * c'est soit une visionKey, une filterKey ou une measure
 * Permet de choisir quoi mettre où
 */
export class PivotField {
  public uuid: string; // l'UUID pour identifier la bonne instance lorsqu'un champ est utilisé plusieurs fois dans la même zone
  public code : string = null;

  public fkd: FeiKeyDef; // la clé de champ du FEI, unique
  public arg: FreedataArgsVO = null; // dans le cas d'un FDV, le champ cible

  public widget: IWidget = null; // pour le mode de rendu
  public model: Model = null;
  public contextItem: WidgetConfigurationItemObj; // la conf depuis context

  public globalKind: WIDGET_GLOBAL_KIND; // une constante de mode 

  public isFreeFilter: boolean = false; // c'est un filtre générique (fdvf = ViewData.FILTER_GENERIC || FILTER_ME)

  // #######################################
  // ### gestion des paramètres "Pivot"
  // #######################################

  public wizAgregateFunc: string = null; // fonction d'aggrégat à utiliser en mode pivot
  public style: string = null; // code du style à appliquer
  public styleConso: string = null; // code du style à appliquer pour les lignes de consolidation
  public format: string = null; // chaine de formatage de la donnée (regexp)
  public fdvField: string = null; // champ à utiliser pour les FDVs
  public fdvSubField: string = null; // champ à utiliser pour les FDVs de type OBJECT
  public fdvSearch: string = null; // champs complémentaires pour pop de search
  public filtHide: boolean; // masque le filtre pour les utilisateurs
  public mandatoryFilter: boolean; // filtre obligatoire
  public filtOnlyExist: boolean = true; // n'affiche que les éléments existants dans le filtre

  public totalAgregateFunc: string = null; // fonction d'aggrégat à utiliser en mode pivot
  public totalAgregateMeasure: string = null; // mesure d'aggrégat à utiliser en mode pivot
  public totalFormat: string = null; // chaine de formatage de la donnée (regexp)


  public headerStyle: string = null; // style de l'entête
  public headColorField: string = null; // field style de l'entête (background)
  public headSubField: string = null; // sopus-titre de l'entête
  public masterRowStyle: string = null; // rendu des lignes de cumul
  public headInherit: boolean = false; // héritage de style depuis la colonne parente

  public cellStyle: string = null; // style des cellules
  public measureStyle: string = null; // style par mesure
  public colRezisable: boolean = true;
  public colMinSize: number = null;
  public colSize: number = null;
  public colAutoSize: boolean = false; // flex 1
  public exportable: boolean = false; // inclus dans les exports Excel
  public hiddenCol: boolean = false; // colonne masquée

  public parentGroup: string = null; // code du groupe de colonne parent
  public readOnly: boolean = false; // readOnly
  public massSplit: boolean = false; // mode split sur massEdit

  public order: number = 0; // sort pour panel conf Forms

  public headerAlign: string = null; // alignement des entêtes
  public cellAlign: string = null; // alignement des cellules

  public usedAsFilter: boolean = false; // le champ est utilisé comme filtre
  public usedAsRow: boolean = false; // le champ est utilisé comme clé de ligne
  public usedAsColumn: boolean = false; // le champ est utilisé comme clé de colonne
  public usedAsData: boolean = false; // le champ est utilisé comme donnée
  public usedAsIntegrityControl = false; // utilisé en contrôle d'intégrité

  public popUpMode: string = null; // le mode de gestion pour les popUps d'édition des widgets (WIDGET_KIND)


  public visible: boolean = true; // colonne visible
  public pinned: string = null; // champ verrouillé à gauche ('left')
  public sortable: boolean = false; // triable

  // settings complémentaires (gestion des workflows, conf de grid gigognes)
  public pmdexParams: string = null;


  /**
   * 
   * @param widget source FD / FREE / BASELINE
   * @param contextItem la conf
   * @param fkd en cas de widget Baseline
   * @param arg en cas de widget FD / Free
   */
  constructor(widget: IWidget, contextItem: WidgetConfigurationItemObj, fkd: FeiKeyDef, arg: FreedataArgsVO ) {
    this.uuid = WizMathUtils.createUUID();
    this.widget = widget;
    this.model = this.widget?.model;
    this.contextItem = contextItem;
    
    this.fkd = fkd;
    this.arg = arg;

    this.isFreeFilter = this.contextItem?.cod === ViewData.FILTER_GENERIC || this.contextItem?.cod === ViewData.FILTER_ME;

    if(this.isFreeFilter) {
      this.globalKind = WIDGET_GLOBAL_KIND.NONE;
      this.code = this.contextItem?.cod;
      this.order = this.contextItem ? this.contextItem.ord : 0;
      // alimentation du fake FKD
      this.fkd = !!contextItem?.cod && !!widget ? widget.model.freedataEngine.getPkdFromFD(widget.fei as FeiVO, contextItem.cod) : null;

    } else if(fkd) { // c'est de la conf Baseline
      this.globalKind = WIDGET_GLOBAL_KIND.BASELINE;
      this.code = this.fkd.code;
      this.order = fkd.sortKey;
    } else if (arg) { // c'est de la conf FreedataValue
      this.globalKind = WIDGET_GLOBAL_KIND.FREEDATA;
      this.code = this.arg.code;
      this.order = this.contextItem ? this.contextItem.ord : 0;
      // alimentation du fake FKD
      this.fkd = contextItem.cod && !!widget ? widget.model.freedataEngine.getPkdFromFD(widget.fei as FeiVO, contextItem.cod) : null;

    } else { // c'est un free
      this.globalKind = WIDGET_GLOBAL_KIND.NONE;
      this.code = this.contextItem?.cod;
      // if(this.code === ViewData.FILTER_GENERIC) {
      //   this.isFreeFilter = true;
      // }

      this.order = this.contextItem ? this.contextItem.ord : 0;
      // alimentation du fake FKD
      this.fkd = !!contextItem?.cod && !!widget ? widget.model.freedataEngine.getPkdFromFD(widget.fei as FeiVO, contextItem.cod) : null;

    }

    // conf champ@field
    // if(this.contextItem?.fdvf === ViewData.FILTER_GENERIC) {
    //   this.isFreeFilter = true;
    // } else 
    if(this.contextItem?.fdvf) {
      let parts: string[] = this.contextItem.fdvf.split(WizConstants.FD_PARTS_SEPARATOR);
      this.fdvField = parts[0];
      this.fdvSubField = parts.length === 2 ? parts[1] : null;
    }

    this.wizAgregateFunc = this.contextItem ? this.contextItem.agg : null;
    if(! this.wizAgregateFunc && fkd?.isMeasure && fkd?.measure?.defaultAgg) {
      this.wizAgregateFunc = fkd.measure.defaultAgg;
    }

    // mode d'aggrégat pour les cumuls
    this.totalAgregateFunc = this.contextItem ? this.contextItem.totAgg : null;
    if(! this.totalAgregateFunc && fkd?.isMeasure && fkd?.measure?.defaultAgg) {
      this.totalAgregateFunc = fkd.measure.defaultAgg;
    }

    this.totalAgregateMeasure = this.contextItem ? this.contextItem.totAggM : null;

    this.totalFormat = this.contextItem ? this.contextItem.totFmt : null;

    this.style = this.contextItem ? this.contextItem.sty : null;
    this.styleConso = this.contextItem ? this.contextItem.masterRowStyle : null;
    this.headerStyle = this.contextItem ? this.contextItem.hsty : null;
    this.cellStyle = this.contextItem ? this.contextItem.csty : null;
    this.format = this.contextItem ? this.contextItem.fmt : null;
    this.fdvSearch = this.contextItem ? this.contextItem.fdvSearch : null;
    this.filtHide = this.contextItem ? this.contextItem.filtHide : null;
    this.mandatoryFilter = this.contextItem ? this.contextItem.mandatory : null;
    this.filtOnlyExist = this.contextItem ? this.contextItem.filtOnEx : null;
    this.colRezisable = this.contextItem ? this.contextItem.casiz : true;
    this.colAutoSize = this.contextItem ? this.contextItem.cauto : false;
    this.colMinSize = this.contextItem ? this.contextItem.cmsiz : null;
    this.colSize = this.contextItem ? this.contextItem.csiz : null;
    this.parentGroup = this.contextItem ? this.contextItem.pgrp : null;
    this.readOnly = this.contextItem ? !!this.contextItem.ro : false;
    this.headerAlign = this.contextItem ? this.contextItem.heada : null;
    this.cellAlign = this.contextItem ? this.contextItem.cella : null;
    this.massSplit = this.contextItem ? this.contextItem.mems : null;
    this.measureStyle = this.contextItem ? this.contextItem.msty : null;
    this.exportable = this.contextItem ? this.contextItem.exportable : false; // c'est PAS d'export en fait
    this.hiddenCol = this.contextItem ? this.contextItem.hiddenCol : false; 
    this.headColorField = this.contextItem ? this.contextItem.headColorField : null; 
    this.headSubField = this.contextItem ? this.contextItem.headSubField : null; 
    this.masterRowStyle = this.contextItem ? this.contextItem.masterRowStyle : null; 
    this.headInherit = this.contextItem ? this.contextItem.headInherit : false; 

    this.pmdexParams = this.contextItem ? this.contextItem.pmdexParams : null;
  }

  /** une visionKey ou une visionFilterKey */
  public get isKey(): boolean {
    return this.fkd.isKey;
  }

  /** une visionFilterKey */
  public get isFilterKey(): boolean {
    return this.fkd.isFilterKey;
  }

  /**
   * accesseur sur le fullCode (baseline uniquement)
   */
  public get fullCode(): string {
    return this.fkd.fullCode;
  }

  /** accesseur sur le mode fist (baseline uniquement) */
  public get mode(): string {
    return this.fkd.mode;
  }

  /**
   * accesseur sur le label
   */
  public get label(): string {
    if(this.contextItem?.fdvf === ViewData.FORM_HTML) {
      return WizTranslationManager.getInstance().translateKey('$.common.free.content.$');
    }
    return !!this.contextItem?.lbl ? this.contextItem.lbl : null;
  }
  public set label(value: string) {
    if (this.contextItem) {
      this.contextItem.lbl = value;
    }
  }

  /** accesseur pour rendu dans la pop d'édition. Affiche fx sur les mesures non éditables */
  public get isFormula(): boolean {
    return this.fkd.isFormula;
  }

  /** formule mode I */
  public get isI(): boolean {
    return this.fkd.isI;
  }

  /** formule mode S */
  public get isS(): boolean {
    return this.fkd.isS;
  }

  /** formule mode T */
  public get isT(): boolean {
    return this.fkd.isT;
  }

  /** formule mode F */
  public get isF(): boolean {
    return this.fkd.isF;
  }

  // utilisé à minima une fois
  public get isUsed(): boolean {
    return this.usedAsData || this.usedAsRow || this.usedAsColumn || this.usedAsFilter || this.usedAsIntegrityControl;
  }

  /** ce champ est éditable */
  public get editable(): boolean {
    return this.fkd.editable;
  }

  /**
   * mode colonne, (baseline uniquement)
   */
  public get groupLabel(): string {
    let result:string = '';
    if (!!this.parentGroup) {
      result = '(' + this.parentGroup +') ';
    }
    result += this.labelOrDefaultLabel;
    return result;
  }

  /**
   * affichage des noms & code de groupe dans la drop de choix des groupes (pour éviter label vide)
   */
  public get dropGroupLabel(): string {
    let result:string = '(' + this.code +') ';
    result += this.labelOrDefaultLabel;
    return result;
  }

  /**
   * tooltip pour les pops de settings
   */
  public get tooltipLabel(): string {
    let result:string = null;
    if (!!this.fkd) {
      result = this.fkd?.fullCode;
    }
    return result;
  }

  /** retourne le label */
  public get labelOrDefaultLabel(): string {
    let result:string = '';
 
    if (!!this.label) {
      result = this.label;
    } else if(this.isFreeFilter) {
      if(this.code === ViewData.FILTER_GENERIC) {
        result = WizTranslationManager.getInstance().translateKey('$.common.free.filter.$');
      } else if(this.code === ViewData.FILTER_ME){
        result = WizTranslationManager.getInstance().translateKey('$.common.free.filter.me.$');
      } else {
        result = '';
      }
      
    }
    else {
      if(this.globalKind === WIDGET_GLOBAL_KIND.BASELINE) {
        result = this.model.feiEngine.getFeiKeyDefLabel(this.fkd);
      } else if(this.globalKind === WIDGET_GLOBAL_KIND.FREEDATA) {
        return this.arg.trsLabel; // ou label du champ
      }
      
    }
    return result;
    // return this.fullCode; // + ' - ' + this.aggOrDefaultFunc();
  }

  /** retourne la fonction d'aggrégat par défaut pour ce type de measure */
  public aggOrDefaultFunc(): string {
    if (this.wizAgregateFunc) {
      return this.wizAgregateFunc;
    }
    return this.model.dataEngine.pkdDefaultAggregateFunction(this.fkd.datakind);
  }
}
